<template>
  <!-- START : Customer Details-->
  <section class="wc-my-account">
    <div class="wc-my-account__card" :aria-label="$t('customerInfo')">
      <div class="wc-my-account__customer-info">
        <div
          class="wc-my-account__customer-meta"
          :class="{ 'wc-my-account__customer-meta--customer-assoc': isEmployee && isCustomer }"
        >
          <h5 class="wc-my-account__name" :aria-label="$t('name')">
            <WCUserGreetingV2 :layout="userGreetingLayout.ACCOUNT" />
          </h5>
          <p v-if="customerId" class="wc-my-account__id" :aria-label="$t('customerId')">
            {{ $t('memberID') }}: {{ customerId }}
          </p>
        </div>
        <div>
          <div class="sidebar-v2__signout-wrap">
            <button
              v-if="isCustomer || isEmployee"
              class="btn btn-link p-0 w-100 text-center sidebar-v2__signout-button"
              @click="logoutAndClose"
            >
              <WCSvgIcon
                class="side-menu-icon"
                name="sign-out-sign-out-of-all-devices"
                icon-size="xl"
              /><span class="sidebar-v2__signout-label">
                {{ isCustomer ? $t('signOut') : $t('logOut') }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="wc-my-account__card pt-0 pb-0"
      :class="{ 'wc-my-account__card--single-option': accountItems.length === 1 }"
      v-if="accountItems?.length > 0"
    >
      <div
        v-for="(item, index) in accountItems"
        :key="item.menuRoute || item.menuName"
        class="wc-my-account__card-item"
        :class="{ 'no-top-border': index === 0 }"
      >
        <WCSidebarMenuItemV2 :menu="item" :isDisplayChevronRight="false" />
      </div>
    </div>
    <div v-if="isCustomer" class="wc-my-account__card wc-my-account__card--single-option pt-0 pb-0">
      <div class="wc-my-account__card-item no-top-border">
        <button
          class="wc-my-account__card-button"
          @click="signOutOfAllDevicesAndClose"
          data-testid="signOutOfAllDevicesButton"
        >
          <span class="wc-my-account__row-main">
            <WCSvgIcon
              class="side-menu-icon"
              name="sign-out-sign-out-of-all-devices"
              icon-size="lg"
            />
            <span class="sidebar-v2-item__label">
              {{ $t('signOutOfAllDevices') }}
            </span>
          </span>
        </button>
      </div>
    </div>
    <div
      v-if="isDeleteEnabled && isCustomer"
      class="wc-my-account__card wc-my-account__card--single-option pt-0 pb-0"
    >
      <div class="wc-my-account__card-item no-top-border">
        <button
          class="wc-my-account__card-button"
          @click="openDeleteAccountModalAndClose"
          data-testid="deleteAccountButton"
        >
          <div class="wc-my-account__row-main text-danger" data-testid="deleteAccountButton">
            <WCSvgIcon class="side-menu-icon text-danger" name="delete-account" icon-size="lg" />
            <span class="icon-menu--margin">{{ $t('deleteAccount') }}</span>
          </div>
        </button>
      </div>
    </div>
  </section>
  <!-- END : Customer Details-->
</template>

<script>
import { mapGetters } from 'vuex';
import { USER_ROLES, USER_GREETING_LAYOUT } from '@/constants/AppConstants';
import useSignOut from '@/modules/user/composables/useSignOut';
import EmployeeMixin from '@/modules/employee/mixins/EmployeeMixin';
import AccountDeleteMixin from '@/modules/user/mixins/AccountDeleteMixin';
import UserMixin from '@/modules/user/mixins/UserMixin';
import NameFormatMixin from '@/mixins/NameFormatMixin';
import pushNotificationService from '@/services/pushNotifications/pushNotification.service';
import useFederatedLogin from '@/composables/useFederatedLogin';
import SideMenuService from '@/modules/sidebar/services/SideMenuService';
import PushNotificationSupportParams from '@/models/entities/push-notifications/push-notification-support-params';
import WCUserGreetingV2 from '@/components/WCUserGreetingV2/WCUserGreetingV2.vue';
import WCSidebarMenuItemV2 from '../WCSidebarMenuItemV2/WCSidebarMenuItemV2.vue';

export default {
  setup() {
    const { isFederatedLoginEnabled } = useFederatedLogin();
    return {
      signOut: useSignOut(),
      isFederatedLoginEnabled,
      userGreetingLayout: USER_GREETING_LAYOUT,
    };
  },
  components: {
    WCSidebarMenuItemV2,
    WCUserGreetingV2,
  },
  mixins: [UserMixin, NameFormatMixin, EmployeeMixin, AccountDeleteMixin],
  methods: {
    async logoutAndClose() {
      await this.logout();
      SideMenuService.close();
    },
    async signOutOfAllDevicesAndClose() {
      SideMenuService.close();
      await this.signOutOfAllDevices();
    },
    signOutOfAllDevices() {
      this.signOut.signOutAllDevices();
    },
    async openDeleteAccountModalAndClose() {
      SideMenuService.close();
      await this.openDeleteAccountModal();
    },
  },
  computed: {
    ...mapGetters({
      isCustomer: 'user/isCustomer',
      isEmployee: 'user/isEmployee',
    }),
    customerId() {
      return this.user?.num;
    },
    pushNotificationsEnabled() {
      try {
        const pushNotificationSupportParams = new PushNotificationSupportParams({
          enabledPlatforms: this.$configuration.enabledPlatforms,
          webPushConfig: this.$configuration.webPushConfig,
          publicVapidKey: this.$configuration.publicVapidKey,
          isCustomer: this.isCustomer,
          isEmployee: this.isEmployee,
        });

        return pushNotificationService.isPushNotificationsSupported(pushNotificationSupportParams);
      } catch (error) {
        console.debug('Push Notifications are not supported:', error);
        return false;
      }
    },
    accountItems() {
      const items = [];

      if (this.isEmployee) {
        return items;
      }

      if (this.$configuration.walletEnabled) {
        items.push({
          menuName: this.$t('wallet'),
          menuIcon: 'wallet',
          menuRoute: '/me/wallet',
          unauthorizedUsers: [USER_ROLES.EMPLOYEE],
        });
      }
      if (this.pushNotificationsEnabled) {
        items.push({
          menuName: this.$t('notifications'),
          menuIcon: 'bell',
          menuRoute: '/me/notifications',
          unauthorizedUsers: [USER_ROLES.EMPLOYEE],
        });
      }
      items.push({
        menuName: this.$t('accountSettings'),
        menuIcon: 'settings',
        menuRoute: '/me/edit',
        unauthorizedUsers: [USER_ROLES.EMPLOYEE],
      });
      if (this.isFederatedLoginEnabled(this.$configuration)) {
        items.push({
          menuName: this.$t('connectedAccounts'),
          menuIcon: 'connected-accounts',
          menuRoute: '/me/connectedAccounts',
          unauthorizedUsers: [USER_ROLES.EMPLOYEE],
        });
      }
      if (this.$configuration.cmsEnabled) {
        items.push({
          menuName: this.$t('membership'),
          menuIcon: 'membership',
          menuRoute: '/me/coop',
          unauthorizedUsers: [USER_ROLES.EMPLOYEE],
        });
      }
      return items;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-my-account {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.wc-my-account__title {
  color: var(--dark);
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
}

.wc-my-account__card {
  border: 0.5px solid var(--dark-lighter-5);
  border-radius: 0.5rem;
  background: var(--white, $white);
  padding: 1rem 1.25rem;
}

.wc-my-account__customer-meta {
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
}

.wc-my-account__customer-meta--customer-assoc {
  background-color: var(--primary-lighter-9, var(--primary));
}

.wc-my-account__name {
  color: var(--dark);
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.wc-my-account__id {
  color: var(--dark);
  font-size: 0.875rem;
  margin: 0.5rem 0 0;
}

.wc-my-account__card-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: var(--sidebar-v2-row-min-height);
  padding: 1rem 0;
  border-top: 1px solid var(--dark-lighter-5);
  cursor: pointer;
  transition: background-color 0.22s ease;
}

.wc-my-account__card--single-option .wc-my-account__card-item {
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -1.25rem;
    right: -1.25rem;
    z-index: -1;
    background-color: transparent;
    transition: background-color 0.22s ease;
  }
}

.wc-my-account__card-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--dark, $dark);
  text-align: left;
}

.wc-my-account__row-main {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
}

.side-menu-icon {
  color: var(--primary, $primary);
  width: 1rem;
}

.sidebar-v2-item__label {
  color: var(--dark, $dark);
  font-size: 1rem;
  line-height: 1.2;
}

.no-top-border {
  border-top: none;
}

@media (hover: hover) and (pointer: fine) {
  .wc-my-account__card-item:hover,
  .wc-my-account__card-item:focus-within {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .wc-my-account__card--single-option .wc-my-account__card-item:hover,
  .wc-my-account__card--single-option .wc-my-account__card-item:focus-within {
    background-color: transparent;
  }

  .wc-my-account__card--single-option .wc-my-account__card-item:hover::before,
  .wc-my-account__card--single-option .wc-my-account__card-item:focus-within::before {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .wc-my-account__card-item.no-top-border:hover::before,
  .wc-my-account__card-item.no-top-border:focus-within::before {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .wc-my-account__card-item:last-child:hover::before,
  .wc-my-account__card-item:last-child:focus-within::before {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
}

@media (hover: none), (pointer: coarse) {
  .wc-my-account__card-item:active {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .wc-my-account__card--single-option .wc-my-account__card-item:active {
    background-color: transparent;
  }

  .wc-my-account__card--single-option .wc-my-account__card-item:active::before {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .wc-my-account__card-item.no-top-border:active::before {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .wc-my-account__card-item:last-child:active::before {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
}

.wc-my-account__customer-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.sidebar-v2__signout-button {
  display: flex;
  flex-direction: column; /* stack icon + text vertically */
  align-items: center; /* center horizontally */
  justify-content: center;
  gap: 0.25rem; /* space between icon & text */
}

.sidebar-v2__signout-icon {
  font-size: 1.25rem; /* adjust as needed */
}

.sidebar-v2__signout-label {
  display: block;
}
</style>
